import { useState, useEffect, useRef } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import { CalendarIcon, DateCalendar, LocalizationProvider} from '@mui/x-date-pickers-latest';
import { AdapterDayjs } from '@mui/x-date-pickers-latest/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import weekdayPlugin from 'dayjs/plugin/weekday';
import localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/es';
import { Day } from './CustomPickersDay';
import { WeekPickerProps } from './types';

dayjs.extend(isBetweenPlugin);
dayjs.extend(weekdayPlugin);
dayjs.extend(localeData);

dayjs.locale('es');

export function WeekPicker({ 
  onChange,
  value,
  minDate = dayjs('2020-01-01'),
  maxDate = dayjs('2025-12-31'),
}: WeekPickerProps) {
  const [hoveredDay, setHoveredDay] = useState<Dayjs | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (value) {
      const startTs = value.weekday(0).startOf('day').unix();
      const stopTs = value.weekday(6).endOf('day').unix()

      if (!value.isSame(dayjs.unix(startTs), 'day')) {
        onChange({
          startTs,
          stopTs
        });
      }
    }
  }, [value, onChange])

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }

  const handleClose = () => {
    setAnchorEl(null);
  }

  const handleChange = (newValue: Dayjs | null) => {
    if (newValue) {
      const startTs = newValue.weekday(0).startOf('day').unix();
      const stopTs = newValue.weekday(6).endOf('day').unix()

      onChange({
        startTs,
        stopTs
      });
    }
  }

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          border: 1,
          borderRadius: 1,
          marginLeft: 'auto'
        }}
      >
        <CalendarIcon />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='es'>
          <DateCalendar
            minDate={minDate}
            maxDate={maxDate}
            value={value}
            onChange={handleChange}
            showDaysOutsideCurrentMonth
            displayWeekNumber
            slots={{ day: Day }}
            slotProps={{
              day: (ownerState) => ({
                selectedDay: value,
                hoveredDay,
                onPointerEnter: () => setHoveredDay(ownerState.day),
                onPointerLeave: () => setHoveredDay(null),
              }) as any,
            }}
          />
        </LocalizationProvider>
      </Popover>
    </>
  );
}
